import Modal from '../Modal';
import { useContext, useEffect, useState } from 'react';
import { LocalizationContext } from 'context/localization.context';
import Icon from '../Icon';
import Button from '../Button';
import { useDispatch } from 'react-redux';
import { AddToCartModel } from 'types/cart-types';
import { AddToCart } from 'store/modules/cart';
import { toast } from 'react-toastify';
import { quickAddValidateCode } from 'api/quickadd';
import { DictionaryModel } from 'types';
import { GenericWarning, ServerError } from 'common/helpers';
import ProductCounter from '../ProductCounter';
import { useMedia } from 'hooks';
interface Props {
	api: DictionaryModel;

	articleCode: string;

	/** Pass a toggle modal callback function */
	onToggle: Function;

	/** fucntion that will close this modal and open scanner again*/
	onScanAgain: Function;
}

interface CartItemProps {
	/** Alias code of an article */
	code: string;

	/** Selected quantity of an article */
	quantity: number;

	/** The name of an article */
	name?: string;

	/** If the entered article code is valid */
	isValid?: boolean;

	itemCode?: string;
}

const QrCodeModal = ({
	onToggle,
	api,
	articleCode,
	onScanAgain,
}: Props): JSX.Element => {
	const [isValidating, setIsValidating] = useState<boolean>(false);
	const [cartItem, setCartItem] = useState<CartItemProps | null>(null);
	const { t }: any = useContext(LocalizationContext);
	const dispatch = useDispatch();
	const isMobile = useMedia('(max-width: 1024px)');

	const handleValidation = async () => {
		if (articleCode) {
			setIsValidating(true);
			try {
				const validatedResponse: CartItemProps[] = await quickAddValidateCode(
					api.validateCodeUrl,
					[{ code: articleCode }]
				);
				validatedResponse && setCartItem(validatedResponse[0]);
				setIsValidating(false);
			} catch (error) {
				setIsValidating(false);
				console.log(error);
			}
		}
	};

	//handle add to cart action, if the article is valid, then dispatch add to cart action, otherwise show error toast
	const handleAddToCart = async () => {
		if (cartItem && cartItem.code && cartItem.isValid) {
			let articleList: AddToCartModel[] = [];
			articleList.push({
				itemCode: cartItem.code,
				quantity: cartItem.quantity ?? 1,
				source: 'qrcode',
			});
			try {
				await dispatch(AddToCart('POST', articleList));
				toast(t('quickadd/transfersuccess'), {
					type: toast.TYPE.SUCCESS,
				});
			} catch (err) {
				if (err instanceof ServerError) {
					toast(t('shared/toast/cartupdateerror'), {
						type: toast.TYPE.ERROR,
					});
				} else if (err instanceof GenericWarning) {
					toast(err.message, { type: toast.TYPE.WARNING });
				}
			} finally {
				setCartItem(null);
				onToggle();
			}
		}
	};

	const handleQuantityChange = (itemcode: string, quantity: number) => {
		if (cartItem && cartItem.code === itemcode) {
			setCartItem({ ...cartItem, quantity });
		}
	};

	useEffect(() => {
		handleValidation();
	}, []);
	return (
		<div className="p-3">
			{isValidating ? (
				<div className="text-center">
					<Icon icon="loader" animate="spin" size={5} aria-hidden={true} />
				</div>
			) : (
				<Modal
					toggleModal={() => onToggle && onToggle()}
					heading={'Add Article To Cart'}
					title={'Add Article To Cart'}
					width="max"
				>
					<div className="flex flex-col gap-3 ">
						<div className="flex gap-2 flex-col mb-6">
							<p className="text-p  flex items-center">
								{t('cartpage/flexishop/articlenumber')} :
								<span className="text-p font-semibold inline-block ml-2 uppercase">
									#{cartItem?.code}
								</span>
							</p>
							<p className="text-p  flex items-center">
								{t('cartpage/flexishop/articlename')}
								<span className="ml-4 text-h4 text-blue font-medium">
									{cartItem?.name}
								</span>
							</p>
						</div>
						{!cartItem?.isValid && (
							<p className="text-red text-p">
								{t('cartpage/flexishop/selectedarticleinvalid')}
							</p>
						)}

						{cartItem?.isValid && (
							<ProductCounter
								id={`Counter-${cartItem?.code}`}
								productName={cartItem?.name!}
								quantity={cartItem?.quantity ?? 1}
								itemCode={cartItem?.itemCode ?? cartItem?.code!}
								onChange={(itemCode: string, count: number) => {
									if (handleQuantityChange)
										handleQuantityChange(itemCode, count);
								}}
							/>
						)}
						<div className="flex gap-4 flex-wrap">
							{cartItem?.isValid && (
								<Button
									type="button"
									buttonColor="blue"
									onClick={handleAddToCart}
								>
									<span aria-hidden={true}>
										{isMobile
											? t('shared/articlerow/add')
											: t('shared/articlerow/addtocart')}
									</span>

									<span className="sr-only">
										{`${t('shared/articlerow/add')} ${cartItem.name}-${
											cartItem.code
										}`}
									</span>
									<Icon icon="cart" size={1.25} aria-hidden="true" />
								</Button>
							)}
							<Button
								buttonColor="blue"
								onClick={(e) => {
									e.preventDefault();
									onScanAgain();
								}}
							>
								{t('shared/qrcode/scanagain')}
							</Button>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};
export default QrCodeModal;
